import { Circle, FabricText, Group, Line, Path, Polygon, Rect, Shadow, Textbox } from 'fabric';
import type { FabricObject } from 'fabric';
import { registerRecipe } from '../registry';
import type { FillValue, ToolRecipe } from '../types';
import { tagSmartObject } from '../types';
import { applyFill } from '../fill';

/**
 * Decor recipes: starbursts, borders, bubbles, ribbons and scatter pieces.
 *
 * Determinism contract: every "random" placement flows through mulberry32
 * seeded from params.seed, so identical params always regenerate an
 * identical object (regenerateSmartObject relies on this).
 */

/** Tiny seeded PRNG — Math.random() would break param → output determinism. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), a | 1);
    t = (t + Math.imul(t ^ (t >>> 7), t | 61)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/* Param coercion — params may arrive as strings from JSON/editor fields;
   generate() must never throw on bad input. */
function num(v: unknown, fallback: number): number {
  if (v === null || v === undefined || v === '') return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function str(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

/** json param → string[] of colors; accepts an array or a JSON string. */
function colorList(v: unknown, fallback: string[]): string[] {
  let raw: unknown = v;
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return fallback;
    }
  }
  if (!Array.isArray(raw)) return fallback;
  const colors = raw.filter((c): c is string => typeof c === 'string' && c.length > 0);
  return colors.length > 0 ? colors : fallback;
}

/**
 * Best solid stand-in for a 'fill'-type param — used where a recipe needs a
 * plain color string (fold shading, secondary pieces) out of a possibly-rich
 * FillValue. Plain strings pass through unchanged (back-compat).
 */
function fillColor(v: unknown, fallback: string): string {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object') {
    const fv = v as { type?: unknown; color?: unknown; from?: unknown };
    if (fv.type === 'solid' && typeof fv.color === 'string') return fv.color;
    if (fv.type === 'linear-gradient' && typeof fv.from === 'string') return fv.from;
  }
  return fallback;
}

/** Multiply hex RGB by a factor (fold shading). Non-hex input passes through. */
function shade(color: string, factor: number): string {
  const m = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(color.trim());
  if (!m) return color;
  let hex = m[1];
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const n = parseInt(hex, 16);
  const ch = (v: number) =>
    clamp(Math.round(v * factor), 0, 255)
      .toString(16)
      .padStart(2, '0');
  return `#${ch((n >> 16) & 255)}${ch((n >> 8) & 255)}${ch(n & 255)}`;
}

/** Round for path strings — keeps serialized JSON compact and stable. */
function f(v: number): number {
  return Math.round(v * 100) / 100;
}

/** Group children (absolute coords) and normalize to left/top 0,0. */
function pack(objects: FabricObject[]): Group {
  const group = new Group(objects);
  group.set({ left: 0, top: 0 });
  return group;
}

/* ------------------------------------------------------------------ */
/* starburst                                                           */
/* ------------------------------------------------------------------ */

const starburst: ToolRecipe = {
  id: 'starburst',
  domain: 'display',
  tags: ['badge', 'starburst', 'star', 'accent', 'award', 'bulletin-board'],
  name: 'Starburst',
  category: 'decor',
  version: 1,
  description: 'Spiky star badge with optional center text ("NEW!", "WOW!").',
  defaults: {
    points: 12,
    outerRadius: 120,
    innerRadiusPct: 55,
    fill: '#ffd600',
    strokeColor: '#f57f17',
    strokeWidth: 3,
    rotation: 0,
    text: 'NEW!',
    fontFamily: 'Arial',
    fontSize: 36,
    textColor: '#d32f2f',
  },
  schema: [
    { key: 'points', label: 'Points', type: 'number', min: 5, max: 24, step: 1 },
    { key: 'outerRadius', label: 'Outer radius', type: 'number', min: 20, max: 600, step: 1 },
    {
      key: 'innerRadiusPct',
      label: 'Inner radius %',
      type: 'number',
      min: 5,
      max: 95,
      step: 1,
      advanced: true,
      help: 'Inner radius as % of outer — lower = spikier.',
    },
    { key: 'fill', label: 'Fill', type: 'fill', help: 'Solid, gradient, or texture.' },
    { key: 'strokeColor', label: 'Stroke color', type: 'color' },
    {
      key: 'strokeWidth',
      label: 'Stroke width',
      type: 'number',
      min: 0,
      max: 20,
      step: 1,
      advanced: true,
    },
    {
      key: 'rotation',
      label: 'Rotation°',
      type: 'number',
      min: -180,
      max: 180,
      step: 1,
      advanced: true,
    },
    { key: 'text', label: 'Text', type: 'text', help: 'Leave empty for no label.' },
    { key: 'fontFamily', label: 'Font', type: 'font' },
    {
      key: 'fontSize',
      label: 'Font size',
      type: 'number',
      min: 8,
      max: 200,
      step: 1,
      advanced: true,
    },
    { key: 'textColor', label: 'Text color', type: 'fill', help: 'Solid, gradient, or texture.' },
  ],
  // Smart Tools modal exemplar (SM-A, 2026-07-23): the badge word + its fill
  // are the whole decision — hero them; the sectionless remainder reads best
  // two-up in the wide modal.
  modal: { hero: ['text', 'fill'], columns: 2 },
  // Honest band values: fewer/fatter spikes + heavy outline for K-2 bulletin
  // boards; more/sharper spikes and a slim display face (Bebas Neue) as the
  // audience matures. innerRadiusPct DOWN = spikier (schema help).
  gradeBands: {
    K2: { points: 8, innerRadiusPct: 62, strokeWidth: 5, fontSize: 44, fontFamily: 'Fredoka' },
    G35: { points: 10, innerRadiusPct: 58, strokeWidth: 4, fontSize: 40, fontFamily: 'Nunito' },
    G68: { points: 12, innerRadiusPct: 55, strokeWidth: 3, fontSize: 36, fontFamily: 'Lexend' },
    G912: { points: 16, innerRadiusPct: 48, strokeWidth: 2, fontSize: 30, fontFamily: 'Bebas Neue' },
  },
  async generate(params) {
    const points = clamp(Math.round(num(params.points, 12)), 5, 24);
    const outer = Math.max(10, num(params.outerRadius, 120));
    const inner = (outer * clamp(num(params.innerRadiusPct, 55), 5, 95)) / 100;
    const rot = (num(params.rotation, 0) * Math.PI) / 180;
    // Geometry is centered at (outer, outer) so all coords stay positive;
    // rotation is baked into the vertices so regenerate keeps bounds stable.
    const verts: { x: number; y: number }[] = [];
    for (let i = 0; i < points * 2; i++) {
      const r = i % 2 === 0 ? outer : inner;
      const a = rot - Math.PI / 2 + (i * Math.PI) / points;
      verts.push({ x: f(outer + r * Math.cos(a)), y: f(outer + r * Math.sin(a)) });
    }
    const star = new Polygon(verts, {
      stroke: str(params.strokeColor, '#f57f17'),
      strokeWidth: Math.max(0, num(params.strokeWidth, 3)),
      strokeLineJoin: 'miter',
    });
    await applyFill(star, params.fill as FillValue, '#ffd600');
    const children: FabricObject[] = [star];
    const text = str(params.text, '');
    if (text) {
      // Direct cast (the decor fill idiom): a solid string resolves to itself
      // (back-compat), a gradient/texture FillValue to its rich fill.
      const label = new FabricText(text, {
        left: outer,
        top: outer,
        originX: 'center',
        originY: 'center',
        fontFamily: str(params.fontFamily, 'Arial'),
        fontSize: Math.max(6, num(params.fontSize, 36)),
        fontWeight: 'bold',
      });
      await applyFill(label, params.textColor as FillValue, '#d32f2f');
      children.push(label);
    }
    return tagSmartObject(pack(children), starburst, params);
  },
};
registerRecipe(starburst);

/* ------------------------------------------------------------------ */
/* scallop-border                                                      */
/* ------------------------------------------------------------------ */

const scallopBorder: ToolRecipe = {
  id: 'scallop-border',
  domain: 'display',
  tags: ['border', 'frame', 'bulletin-board', 'scallop', 'decor'],
  featured: true,
  name: 'Scallop Border',
  category: 'decor',
  version: 1,
  description: 'Bulletin-board style rectangle with a scalloped (bumpy) edge.',
  defaults: { width: 400, height: 300, scallopRadius: 18, fill: '#42a5f5' },
  schema: [
    { key: 'width', label: 'Width', type: 'number', min: 40, max: 2000, step: 1 },
    { key: 'height', label: 'Height', type: 'number', min: 40, max: 2000, step: 1 },
    {
      key: 'scallopRadius',
      label: 'Scallop radius',
      type: 'number',
      min: 4,
      max: 80,
      step: 1,
      help: 'Size of each edge bump.',
    },
    { key: 'fill', label: 'Fill', type: 'fill', help: 'Solid, gradient, or texture.' },
  ],
  async generate(params) {
    const w = Math.max(40, num(params.width, 400));
    const h = Math.max(40, num(params.height, 300));
    // Radius capped at a quarter of the short side so the inner Rect survives.
    const r = clamp(num(params.scallopRadius, 18), 4, Math.min(w, h) / 4);
    // Resolve the fill ONCE (on the panel), then reuse the resolved value for
    // every bump — a texture must not be fetched per circle.
    const panel = new Rect({ left: r, top: r, width: w - 2 * r, height: h - 2 * r });
    await applyFill(panel, params.fill as FillValue, '#42a5f5');
    const fill = panel.fill ?? '#42a5f5';

    // Circle centers sit on the rect inset by r, so bumps reach the 0..w/0..h
    // bounds exactly. One circle is pinned at each corner (edges include their
    // start corner, exclude their end) and spacing ≤ 1.5r guarantees overlap.
    const x0 = r;
    const y0 = r;
    const x1 = w - r;
    const y1 = h - r;
    const circles: FabricObject[] = [];
    const edge = (ax: number, ay: number, bx: number, by: number) => {
      const len = Math.hypot(bx - ax, by - ay);
      const n = Math.max(1, Math.round(len / (r * 1.5)));
      for (let i = 0; i < n; i++) {
        const cx = ax + ((bx - ax) * i) / n;
        const cy = ay + ((by - ay) * i) / n;
        circles.push(new Circle({ left: f(cx - r), top: f(cy - r), radius: r, fill }));
      }
    };
    edge(x0, y0, x1, y0);
    edge(x1, y0, x1, y1);
    edge(x1, y1, x0, y1);
    edge(x0, y1, x0, y0);

    return tagSmartObject(pack([...circles, panel]), scallopBorder, params);
  },
};
registerRecipe(scallopBorder);

/* ------------------------------------------------------------------ */
/* speech-bubble                                                       */
/* ------------------------------------------------------------------ */

const speechBubble: ToolRecipe = {
  id: 'speech-bubble',
  domain: 'display',
  tags: ['speech-bubble', 'callout', 'comic', 'decor', 'shape'],
  name: 'Speech Bubble',
  category: 'decor',
  version: 1,
  description: 'Rounded speech bubble with a pointer tail and optional text.',
  defaults: {
    width: 280,
    height: 170,
    cornerRadius: 26,
    tailSide: 'bottom-left',
    fill: '#ffffff',
    stroke: '#222222',
    strokeWidth: 3,
    text: 'Hello!',
    fontFamily: 'Arial',
    fontSize: 28,
    textColor: '#222222',
  },
  schema: [
    { key: 'width', label: 'Width', type: 'number', min: 60, max: 2000, step: 1 },
    { key: 'height', label: 'Height', type: 'number', min: 40, max: 2000, step: 1 },
    {
      key: 'cornerRadius',
      label: 'Corner radius',
      type: 'number',
      min: 0,
      max: 100,
      step: 1,
      advanced: true,
    },
    {
      key: 'tailSide',
      label: 'Tail side',
      type: 'select',
      options: [
        { value: 'bottom-left', label: 'Bottom left' },
        { value: 'bottom-right', label: 'Bottom right' },
        { value: 'left', label: 'Left' },
        { value: 'right', label: 'Right' },
      ],
    },
    { key: 'fill', label: 'Fill', type: 'fill', help: 'Solid, gradient, or texture.' },
    { key: 'stroke', label: 'Stroke color', type: 'color' },
    {
      key: 'strokeWidth',
      label: 'Stroke width',
      type: 'number',
      min: 0,
      max: 20,
      step: 1,
      advanced: true,
    },
    { key: 'text', label: 'Text', type: 'multiline', help: 'Leave empty for no text.' },
    { key: 'fontFamily', label: 'Font', type: 'font' },
    {
      key: 'fontSize',
      label: 'Font size',
      type: 'number',
      min: 8,
      max: 120,
      step: 1,
      advanced: true,
    },
    { key: 'textColor', label: 'Text color', type: 'fill', help: 'Solid, gradient, or texture.' },
  ],
  async generate(params) {
    const w = Math.max(60, num(params.width, 280));
    const h = Math.max(40, num(params.height, 170));
    const sw = Math.max(0, num(params.strokeWidth, 3));
    const cr = clamp(num(params.cornerRadius, 26), 0, Math.min(w, h) / 2);
    const stroke = str(params.stroke, '#222222');

    const body = new Rect({ left: 0, top: 0, width: w, height: h, rx: cr, ry: cr, stroke, strokeWidth: sw });
    // Resolve once; the tail reuses the body's resolved fill so the seam
    // where the two meet stays invisible.
    await applyFill(body, params.fill as FillValue, '#ffffff');
    const fill = body.fill ?? '#ffffff';

    // Tail is an OPEN path (fill + stroke): fabric fills it as a closed
    // triangle but strokes only the two V edges, and its base is inset past
    // the body's stroke so the fill hides the seam where they meet.
    const inset = sw + 2;
    const tailLen = Math.min(w, h) * 0.32;
    const base = Math.min(w, h) * 0.34;
    const side = str(params.tailSide, 'bottom-left');
    let d: string;
    if (side === 'left') {
      const yMid = h * 0.62;
      d = `M ${f(inset)} ${f(yMid - base / 2)} L ${f(-tailLen)} ${f(yMid + base * 0.35)} L ${f(inset)} ${f(yMid + base / 2)}`;
    } else if (side === 'right') {
      const yMid = h * 0.62;
      d = `M ${f(w - inset)} ${f(yMid - base / 2)} L ${f(w + tailLen)} ${f(yMid + base * 0.35)} L ${f(w - inset)} ${f(yMid + base / 2)}`;
    } else if (side === 'bottom-right') {
      const xMid = w * 0.75;
      d = `M ${f(xMid - base / 2)} ${f(h - inset)} L ${f(xMid + base * 0.45)} ${f(h + tailLen)} L ${f(xMid + base / 2)} ${f(h - inset)}`;
    } else {
      const xMid = w * 0.25;
      d = `M ${f(xMid - base / 2)} ${f(h - inset)} L ${f(xMid - base * 0.45)} ${f(h + tailLen)} L ${f(xMid + base / 2)} ${f(h - inset)}`;
    }
    const tail = new Path(d, {
      fill,
      stroke,
      strokeWidth: sw,
      strokeLineJoin: 'round',
      strokeLineCap: 'round',
    });

    const children: FabricObject[] = [body, tail];
    const text = str(params.text, '');
    if (text) {
      const pad = 16 + sw;
      const caption = new Textbox(text, {
        left: w / 2,
        top: h / 2,
        originX: 'center',
        originY: 'center',
        width: Math.max(20, w - pad * 2),
        textAlign: 'center',
        fontFamily: str(params.fontFamily, 'Arial'),
        fontSize: Math.max(6, num(params.fontSize, 28)),
      });
      await applyFill(caption, params.textColor as FillValue, '#222222');
      children.push(caption);
    }
    return tagSmartObject(pack(children), speechBubble, params);
  },
};
registerRecipe(speechBubble);

/* ------------------------------------------------------------------ */
/* ribbon-banner                                                       */
/* ------------------------------------------------------------------ */

const ribbonBanner: ToolRecipe = {
  id: 'ribbon-banner',
  domain: 'display',
  tags: ['banner', 'ribbon', 'title', 'header', 'decor'],
  name: 'Ribbon Banner',
  category: 'decor',
  version: 1,
  description: 'Classic forked-tail ribbon with fold shadows and optional text.',
  defaults: {
    width: 360,
    height: 72,
    fill: '#e53935',
    text: 'SPECIAL OFFER',
    fontFamily: 'Arial',
    fontSize: 30,
    textColor: '#ffffff',
  },
  schema: [
    { key: 'width', label: 'Panel width', type: 'number', min: 60, max: 2000, step: 1 },
    { key: 'height', label: 'Panel height', type: 'number', min: 24, max: 400, step: 1 },
    {
      key: 'fill',
      label: 'Fill',
      type: 'fill',
      help: 'Solid, gradient, or texture. Fold shade is derived automatically.',
    },
    { key: 'text', label: 'Text', type: 'text', help: 'Leave empty for no text.' },
    { key: 'fontFamily', label: 'Font', type: 'font' },
    {
      key: 'fontSize',
      label: 'Font size',
      type: 'number',
      min: 8,
      max: 200,
      step: 1,
      advanced: true,
    },
    { key: 'textColor', label: 'Text color', type: 'fill', help: 'Solid, gradient, or texture.' },
  ],
  async generate(params) {
    const w = Math.max(60, num(params.width, 360));
    const h = Math.max(24, num(params.height, 72));
    // Folds need a plain color to shade — derive it from the (possibly rich)
    // fill; for solid-string params this is exactly the old behavior.
    const base = fillColor(params.fill, '#e53935');
    const dark = shade(base, 0.75);

    // Layout: forked ends sit behind the panel, dropped by `drop` so the
    // dark fold triangles read as the ribbon wrapping behind the panel.
    const ext = Math.round(h * 0.85); // horizontal reach of each forked end
    const drop = Math.round(h * 0.3);
    const notch = Math.round(h * 0.4); // depth of the V cut
    const foldW = Math.round(h * 0.3);
    const totalW = w + 2 * ext;
    const px = ext; // panel left edge

    // Resolve the band fill ONCE (on the panel); the forked ends reuse the
    // resolved value so a texture isn't fetched three times.
    const panel = new Rect({ left: px, top: 0, width: w, height: h });
    await applyFill(panel, params.fill as FillValue, '#e53935');
    const fill = panel.fill ?? base;

    const leftEnd = new Polygon(
      [
        { x: px, y: drop },
        { x: 0, y: drop },
        { x: notch, y: drop + h / 2 },
        { x: 0, y: drop + h },
        { x: px, y: drop + h },
      ],
      { fill },
    );
    const rightEnd = new Polygon(
      [
        { x: px + w, y: drop },
        { x: totalW, y: drop },
        { x: totalW - notch, y: drop + h / 2 },
        { x: totalW, y: drop + h },
        { x: px + w, y: drop + h },
      ],
      { fill },
    );
    // Folds: only the part below y = h shows (the panel covers the rest).
    const leftFold = new Polygon(
      [
        { x: px, y: h },
        { x: px + foldW, y: h },
        { x: px, y: h + drop },
      ],
      { fill: dark },
    );
    const rightFold = new Polygon(
      [
        { x: px + w, y: h },
        { x: px + w - foldW, y: h },
        { x: px + w, y: h + drop },
      ],
      { fill: dark },
    );

    const children: FabricObject[] = [leftEnd, rightEnd, leftFold, rightFold, panel];
    const text = str(params.text, '');
    if (text) {
      const label = new FabricText(text, {
        left: px + w / 2,
        top: h / 2,
        originX: 'center',
        originY: 'center',
        fontFamily: str(params.fontFamily, 'Arial'),
        fontSize: Math.max(6, num(params.fontSize, 30)),
        fontWeight: 'bold',
      });
      await applyFill(label, params.textColor as FillValue, '#ffffff');
      children.push(label);
    }
    return tagSmartObject(pack(children), ribbonBanner, params);
  },
};
registerRecipe(ribbonBanner);

/* ------------------------------------------------------------------ */
/* torn-paper                                                          */
/* ------------------------------------------------------------------ */

const tornPaper: ToolRecipe = {
  id: 'torn-paper',
  domain: 'display',
  tags: ['paper', 'texture', 'accent', 'collage', 'decor'],
  name: 'Torn Paper',
  category: 'decor',
  version: 1,
  description: 'Paper strip with ragged top/bottom edges and a soft shadow.',
  defaults: { width: 320, height: 200, fill: '#fffef5', roughness: 10, seed: 1 },
  schema: [
    { key: 'width', label: 'Width', type: 'number', min: 40, max: 2000, step: 1 },
    { key: 'height', label: 'Height', type: 'number', min: 40, max: 2000, step: 1 },
    { key: 'fill', label: 'Fill', type: 'fill', help: 'Solid, gradient, or texture.' },
    { key: 'roughness', label: 'Roughness', type: 'number', min: 1, max: 40, step: 1, help: 'Tear depth in px.' },
    { key: 'seed', label: 'Seed', type: 'number', min: 1, max: 99999, step: 1, help: 'Change for a different tear.' },
  ],
  async generate(params) {
    const w = Math.max(40, num(params.width, 320));
    const h = Math.max(40, num(params.height, 200));
    const rough = clamp(num(params.roughness, 10), 1, Math.min(40, h / 4));
    const rand = mulberry32(num(params.seed, 1));

    // Tear offsets push INTO the rect (0..rough) so bounds stay w × h.
    const step = 20;
    let d = `M 0 ${f(rand() * rough)}`;
    for (let x = step; x < w; x += step) d += ` L ${f(x)} ${f(rand() * rough)}`;
    d += ` L ${f(w)} ${f(rand() * rough)}`;
    d += ` L ${f(w)} ${f(h - rand() * rough)}`;
    for (let x = w - step; x > 0; x -= step) d += ` L ${f(x)} ${f(h - rand() * rough)}`;
    d += ` L 0 ${f(h - rand() * rough)} Z`;

    const paper = new Path(d, {
      shadow: new Shadow({ color: 'rgba(0,0,0,0.3)', blur: 10, offsetX: 0, offsetY: 4 }),
    });
    // torn-paper fill upgraded to FillValue (solid/gradient/texture); a solid
    // string resolves to itself, so pre-fill designs render byte-identically.
    await applyFill(paper, params.fill as FillValue, '#fffef5');
    return tagSmartObject(pack([paper]), tornPaper, params);
  },
};
registerRecipe(tornPaper);

/* ------------------------------------------------------------------ */
/* washi-tape                                                          */
/* ------------------------------------------------------------------ */

const washiTape: ToolRecipe = {
  id: 'washi-tape',
  domain: 'display',
  tags: ['tape', 'washi', 'accent', 'collage', 'decor'],
  name: 'Washi Tape',
  category: 'decor',
  version: 1,
  description: 'Semi-transparent tape strip with zigzag torn ends, optional stripes.',
  defaults: {
    width: 220,
    height: 48,
    fill: '#ff8a80',
    angle: -4,
    stripeColor: '',
    stripeWidth: 12,
  },
  schema: [
    { key: 'width', label: 'Length', type: 'number', min: 40, max: 2000, step: 1 },
    { key: 'height', label: 'Thickness', type: 'number', min: 12, max: 200, step: 1 },
    { key: 'fill', label: 'Fill', type: 'fill', help: 'Solid, gradient, or texture.' },
    { key: 'angle', label: 'Angle°', type: 'number', min: -45, max: 45, step: 1 },
    { key: 'stripeColor', label: 'Stripe color', type: 'color', help: 'Leave empty for plain tape.' },
    {
      key: 'stripeWidth',
      label: 'Stripe width',
      type: 'number',
      min: 2,
      max: 100,
      step: 1,
      advanced: true,
    },
  ],
  async generate(params) {
    const w = Math.max(40, num(params.width, 220));
    const h = Math.max(12, num(params.height, 48));

    // Tape outline: straight top/bottom, zigzag "torn" left/right ends.
    // Deterministic teeth (no PRNG needed — geometry only depends on size).
    const zig = clamp(h / 5, 4, 12);
    const teeth = Math.max(2, Math.round(h / 8));
    const stepY = h / teeth;
    const tapePath = (): string => {
      let d = `M ${f(zig)} 0 L ${f(w - zig)} 0`;
      for (let i = 1; i <= teeth; i++) d += ` L ${f(w - (i % 2 === 0 ? zig : 0))} ${f(i * stepY)}`;
      d += ` L ${f(teeth % 2 === 0 ? zig : 0)} ${f(h)}`;
      for (let i = teeth - 1; i >= 1; i--) d += ` L ${f(i % 2 === 0 ? zig : 0)} ${f(i * stepY)}`;
      return `${d} Z`;
    };

    const base = new Path(tapePath());
    await applyFill(base, params.fill as FillValue, '#ff8a80');
    const children: FabricObject[] = [base];

    const stripeColor = str(params.stripeColor, '');
    const stripeW = Math.max(2, num(params.stripeWidth, 12));
    if (stripeColor) {
      // Alternating vertical stripes; the group clipPath below trims them
      // to the zigzag silhouette so they never poke past the torn ends.
      for (let x = stripeW; x < w; x += stripeW * 2) {
        children.push(
          new Rect({
            left: f(x),
            top: 0,
            width: Math.min(stripeW, w - x),
            height: h,
            fill: stripeColor,
          }),
        );
      }
    }

    const group = new Group(children);
    if (stripeColor) {
      // clipPath coords are relative to the group center; the group's bounds
      // equal the tape path bounds (w × h), so shift by half of each.
      const clip = new Path(tapePath());
      clip.set({ left: -w / 2, top: -h / 2 });
      group.clipPath = clip;
    }
    group.set({ opacity: 0.85, angle: num(params.angle, -4), left: 0, top: 0 });
    return tagSmartObject(group, washiTape, params);
  },
};
registerRecipe(washiTape);

/* ------------------------------------------------------------------ */
/* confetti-scatter                                                    */
/* ------------------------------------------------------------------ */

const CONFETTI_PALETTE = ['#ff5252', '#ffb300', '#66bb6a', '#29b6f6', '#7e57c2', '#ff4081'];

const confettiScatter: ToolRecipe = {
  id: 'confetti-scatter',
  domain: 'display',
  tags: ['confetti', 'celebration', 'accent', 'party', 'decor'],
  name: 'Confetti Scatter',
  category: 'decor',
  version: 1,
  description: 'Seeded scatter of colorful confetti dots, squares and slivers.',
  defaults: {
    width: 400,
    height: 300,
    count: 60,
    // Plain array — colorList() still accepts legacy JSON-string values.
    palette: [...CONFETTI_PALETTE],
    seed: 1,
  },
  schema: [
    { key: 'width', label: 'Width', type: 'number', min: 40, max: 4000, step: 1 },
    { key: 'height', label: 'Height', type: 'number', min: 40, max: 4000, step: 1 },
    { key: 'count', label: 'Pieces', type: 'number', min: 1, max: 500, step: 1 },
    {
      key: 'palette',
      label: 'Palette',
      type: 'list',
      itemType: 'color',
      minItems: 1,
      maxItems: 12,
      help: 'Colors the confetti pieces cycle through.',
    },
    { key: 'seed', label: 'Seed', type: 'number', min: 1, max: 99999, step: 1, help: 'Change for a new scatter.' },
  ],
  async generate(params) {
    const w = Math.max(40, num(params.width, 400));
    const h = Math.max(40, num(params.height, 300));
    const count = clamp(Math.round(num(params.count, 60)), 1, 500);
    const palette = colorList(params.palette, CONFETTI_PALETTE);
    const rand = mulberry32(num(params.seed, 1));

    // Invisible bounds rect keeps the group exactly w × h regardless of
    // where pieces land — placement stays stable across regenerations.
    const children: FabricObject[] = [
      new Rect({ left: 0, top: 0, width: w, height: h, fill: 'transparent', strokeWidth: 0 }),
    ];

    for (let i = 0; i < count; i++) {
      const size = 4 + rand() * 10; // pieces stay in the 4-14px range
      const x = rand() * (w - size);
      const y = rand() * (h - size);
      const color = palette[Math.floor(rand() * palette.length)];
      const kind = rand();
      if (kind < 0.4) {
        children.push(new Circle({ left: f(x), top: f(y), radius: f(size / 2), fill: color }));
      } else if (kind < 0.75) {
        children.push(
          new Rect({
            left: f(x),
            top: f(y),
            width: f(size),
            height: f(size * 0.6),
            angle: f(rand() * 360),
            fill: color,
          }),
        );
      } else {
        const a = rand() * Math.PI * 2;
        const len = size * 1.3;
        children.push(
          new Line([f(x), f(y), f(x + Math.cos(a) * len), f(y + Math.sin(a) * len)], {
            stroke: color,
            strokeWidth: 2.5,
            strokeLineCap: 'round',
          }),
        );
      }
    }
    return tagSmartObject(pack(children), confettiScatter, params);
  },
};
registerRecipe(confettiScatter);
